// Cycle Tracker Service - Supabase Integration
// Handles all cycle tracking data with cross-device sync

import { supabase } from '../utils/supabaseClient';

// =====================================================
// PERIOD MANAGEMENT
// =====================================================

/**
 * Get all periods for the current user
 */
export const getUserPeriods = async () => {
  try {
    const { data, error } = await supabase
      .from('cycle_periods')
      .select('*')
      .order('start_date', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching periods:', error);
    return { data: null, error };
  }
};

/**
 * Add a new period
 */
export const addPeriod = async (periodData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('cycle_periods')
      .insert([{
        user_id: user.id,
        start_date: periodData.startDate,
        length: periodData.length,
        flow: periodData.flow
      }])
      .select('*')
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error adding period:', error);
    return { data: null, error };
  }
};

/**
 * Update an existing period
 */
export const updatePeriod = async (periodId, periodData) => {
  try {
    const { data, error } = await supabase
      .from('cycle_periods')
      .update({
        start_date: periodData.startDate,
        length: periodData.length,
        flow: periodData.flow
      })
      .eq('id', periodId)
      .select('*')
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating period:', error);
    return { data: null, error };
  }
};

/**
 * Delete a period
 */
export const deletePeriod = async (periodId) => {
  try {
    const { error } = await supabase
      .from('cycle_periods')
      .delete()
      .eq('id', periodId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting period:', error);
    return { error };
  }
};

// =====================================================
// SYMPTOM MANAGEMENT
// =====================================================

/**
 * Get all symptoms for the current user
 */
export const getUserSymptoms = async (daysBack = 90) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysBack);

    const { data, error } = await supabase
      .from('cycle_symptoms')
      .select('*')
      .gte('symptom_date', cutoffDate.toISOString().split('T')[0])
      .order('symptom_date', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching symptoms:', error);
    return { data: null, error };
  }
};

/**
 * Add or update symptoms for a specific date
 */
export const upsertSymptom = async (symptomData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('cycle_symptoms')
      .upsert({
        user_id: user.id,
        symptom_date: symptomData.date,
        skin_condition: symptomData.skinCondition || null,
        energy_level: symptomData.energyLevel,
        mood: symptomData.mood || [],
        cravings: symptomData.cravings || [],
        pain_level: symptomData.painLevel,
        notes: symptomData.notes || null
      }, {
        onConflict: 'user_id,symptom_date'
      })
      .select('*')
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error upserting symptom:', error);
    return { data: null, error };
  }
};

/**
 * Delete symptoms for a specific date
 */
export const deleteSymptom = async (symptomDate) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('cycle_symptoms')
      .delete()
      .eq('user_id', user.id)
      .eq('symptom_date', symptomDate);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting symptom:', error);
    return { error };
  }
};

// =====================================================
// CYCLE CALCULATIONS
// =====================================================

/**
 * Calculate cycle statistics (average length, next predicted period)
 */
export const calculateCycleStats = (periods) => {
  if (!periods || periods.length === 0) return null;

  // Support both snake_case (from DB) and camelCase (transformed)
  const getStartDate = (period) => period.startDate || period.start_date;
  const getPeriodLength = (period) => period.length || 5; // Default to 5 if not specified

  const sortedPeriods = [...periods].sort((a, b) => 
    new Date(getStartDate(a)) - new Date(getStartDate(b))
  );

  // Calculate average period length (bleeding days)
  const periodLengths = sortedPeriods.map(p => getPeriodLength(p));
  const avgPeriodLength = Math.round(
    periodLengths.reduce((a, b) => a + b, 0) / periodLengths.length
  );

  // For first period, use default 28-day cycle
  if (sortedPeriods.length === 1) {
    const lastPeriod = sortedPeriods[0];
    const nextPredicted = new Date(getStartDate(lastPeriod));
    nextPredicted.setDate(nextPredicted.getDate() + 28);
    
    return {
      avgCycleLength: 28,
      avgPeriodLength: avgPeriodLength,
      nextPredicted,
      lastPeriod: new Date(getStartDate(lastPeriod)),
      isFirstCycle: true
    };
  }

  // Calculate average cycle length (first day to first day)
  const cycleLengths = [];
  for (let i = 1; i < sortedPeriods.length; i++) {
    const diff = Math.floor(
      (new Date(getStartDate(sortedPeriods[i])) - new Date(getStartDate(sortedPeriods[i-1]))) / 
      (1000 * 60 * 60 * 24)
    );
    cycleLengths.push(diff);
  }

  const avgCycleLength = Math.round(
    cycleLengths.reduce((a, b) => a + b, 0) / cycleLengths.length
  );
  
  const lastPeriod = sortedPeriods[sortedPeriods.length - 1];
  const nextPredicted = new Date(getStartDate(lastPeriod));
  nextPredicted.setDate(nextPredicted.getDate() + avgCycleLength);

  return {
    avgCycleLength,
    avgPeriodLength: avgPeriodLength,
    nextPredicted,
    lastPeriod: new Date(getStartDate(lastPeriod)),
    isFirstCycle: false
  };
};

/**
 * Get current cycle phase based on last period
 * 
 * Phase breakdown for a typical 28-day cycle:
 * - Menstrual Phase: Days 1-5 (period, hormones at lowest)
 * - Follicular Phase: Days 6-13 (estrogen rises, energy increases)
 * - Ovulation Phase: Days 14-16 (egg released, peak energy & strength)
 * - Luteal Phase: Days 17-28 (progesterone rises, then PMS)
 */
export const getCurrentPhase = (stats) => {
  if (!stats) {
    return { 
      phase: 'Track your cycle', 
      color: 'text-gray-600', 
      bg: 'bg-gray-100', 
      icon: '🌸',
      description: 'Start tracking to see your cycle phase'
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const lastPeriodDate = new Date(stats.lastPeriod);
  lastPeriodDate.setHours(0, 0, 0, 0);
  
  const daysSinceLastPeriod = Math.floor(
    (today - lastPeriodDate) / (1000 * 60 * 60 * 24)
  );
  
  const cycleDay = daysSinceLastPeriod + 1;
  const cycleLength = stats.avgCycleLength || 28;
  const periodLength = stats.avgPeriodLength || 5;
  
  // Calculate phase boundaries based on cycle length
  // For a 28-day cycle: menstrual 1-5, follicular 6-13, ovulation 14-16, luteal 17-28
  const menstrualEnd = periodLength; // Day 5
  const follicularEnd = Math.round(cycleLength * 0.46); // ~Day 13 for 28-day
  const ovulationEnd = Math.round(cycleLength * 0.57); // ~Day 16 for 28-day
  
  // If cycle day is beyond typical cycle length + 7 days, show waiting for period
  if (cycleDay > cycleLength + 7) {
    return { 
      phase: 'Waiting for Period', 
      color: 'text-gray-600', 
      bg: 'bg-gray-100', 
      icon: '⏳',
      description: 'Period may be late - log when it starts',
      dayInPhase: cycleDay - cycleLength,
      totalPhaseDays: null,
      isMenstruating: false
    };
  }

  // 🩸 Menstrual Phase: Days 1-5
  if (cycleDay >= 1 && cycleDay <= menstrualEnd) {
    return { 
      phase: 'Menstrual Phase', 
      color: 'text-red-600', 
      bg: 'bg-red-50', 
      icon: '🩸',
      description: 'Low energy - gentle movement, walks, rest',
      dayInPhase: cycleDay,
      totalPhaseDays: menstrualEnd,
      isMenstruating: true
    };
  }
  
  // 🌱 Follicular Phase: Days 6-13
  if (cycleDay > menstrualEnd && cycleDay <= follicularEnd) {
    return { 
      phase: 'Follicular Phase', 
      color: 'text-green-600', 
      bg: 'bg-green-50', 
      icon: '🌱',
      description: 'Rising energy - strength training, new programs',
      dayInPhase: cycleDay - menstrualEnd,
      totalPhaseDays: follicularEnd - menstrualEnd,
      isMenstruating: false
    };
  }
  
  // 🌸 Ovulation Phase: Days 14-16
  if (cycleDay > follicularEnd && cycleDay <= ovulationEnd) {
    return { 
      phase: 'Ovulation Phase', 
      color: 'text-pink-600', 
      bg: 'bg-pink-50', 
      icon: '🌸',
      description: 'Peak energy & strength - PBs, hard workouts',
      dayInPhase: cycleDay - follicularEnd,
      totalPhaseDays: ovulationEnd - follicularEnd,
      isMenstruating: false
    };
  }
  
  // 🌙 Luteal Phase: Days 17-28
  const lutealDay = cycleDay - ovulationEnd;
  const lutealLength = cycleLength - ovulationEnd;
  const isLateLuteal = lutealDay > Math.round(lutealLength * 0.5); // Second half of luteal
  
  return { 
    phase: 'Luteal Phase', 
    color: 'text-purple-600', 
    bg: 'bg-purple-50', 
    icon: '🌙',
    description: isLateLuteal 
      ? 'Late luteal - more rest, lower intensity, routine'
      : 'Early luteal - moderate training, good energy',
    dayInPhase: lutealDay,
    totalPhaseDays: lutealLength,
    isMenstruating: false,
    isLateLuteal: isLateLuteal
  };
};

/**
 * Check if period notification should be shown
 */
export const checkPeriodNotification = (stats) => {
  if (!stats || !stats.nextPredicted) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextPeriod = new Date(stats.nextPredicted);
  nextPeriod.setHours(0, 0, 0, 0);

  const daysUntil = Math.floor((nextPeriod - today) / (1000 * 60 * 60 * 24));

  // Show notification if period is 3 days or less away
  if (daysUntil >= 0 && daysUntil <= 3) {
    const message = daysUntil === 0 
      ? '🩸 Your period is predicted to start today!' 
      : daysUntil === 1
      ? '🩸 Your period is predicted to start tomorrow!'
      : `🩸 Your period is predicted to start in ${daysUntil} days`;

    return {
      show: true,
      message,
      daysUntil
    };
  }

  return null;
};

// =====================================================
// MIGRATION HELPER (localStorage to Supabase)
// =====================================================

/**
 * Migrate existing localStorage data to Supabase
 */
export const migrateLocalStorageData = async () => {
  try {
    // Get data from localStorage
    const localCycles = JSON.parse(localStorage.getItem('cycleData') || '[]');
    const localSymptoms = JSON.parse(localStorage.getItem('cycleSymptoms') || '{}');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Migrate periods
    if (localCycles.length > 0) {
      const periodsToInsert = localCycles.map(cycle => ({
        user_id: user.id,
        start_date: cycle.startDate,
        length: cycle.length,
        flow: cycle.flow
      }));

      const { error: periodsError } = await supabase
        .from('cycle_periods')
        .insert(periodsToInsert);

      if (periodsError) throw periodsError;
    }

    // Migrate symptoms
    const symptomEntries = Object.entries(localSymptoms);
    if (symptomEntries.length > 0) {
      const symptomsToInsert = symptomEntries.map(([date, symptom]) => ({
        user_id: user.id,
        symptom_date: date,
        skin_condition: symptom.skinCondition || null,
        energy_level: symptom.energyLevel,
        mood: symptom.mood || [],
        cravings: symptom.cravings || [],
        pain_level: symptom.painLevel,
        notes: symptom.notes || null
      }));

      const { error: symptomsError } = await supabase
        .from('cycle_symptoms')
        .insert(symptomsToInsert);

      if (symptomsError) throw symptomsError;
    }

    // Clear localStorage after successful migration
    localStorage.removeItem('cycleData');
    localStorage.removeItem('cycleSymptoms');

    return { success: true, error: null };
  } catch (error) {
    console.error('Error migrating data:', error);
    return { success: false, error };
  }
};
